//------------------------------------------------------------------------------
// name: useImageURL.ts
// author: mudas
// created: 2025/12/05
//------------------------------------------------------------------------------

import { useComputed } from 'common/hooks';
import { appendImageOp, AppendImageOptions, isVideoFile } from 'common/utils';
import { getExtFromFileName } from 'common/utils/versionDetail';

import DefaultVideoBg from 'common/assets/image/default-card-img.webp';

export interface ImageRefineOption {
  url?: string;
  previewURL?: string;

  options?: AppendImageOptions; // 同时对预览图和原图生效（设置了 previewOptions 时只对原图生效）
  previewOptions?: AppendImageOptions; // 只对预览图生效
}

/**
 * 生成预览图 URL
 * - 一般用在需要小图片列表，数量较多，且尺寸较小的场景
 * @param url 原图 URL
 * @param options 默认缩放填满区域
 */
export const genPreviewURL = (url: string, options?: AppendImageOptions) => {
  return appendImageOp(url, {
    format: 'jpg',
    resize: { mode: 'fill', width: 400, height: 300 },
    ...options,
  });
};

/**
 * 生成大图的压缩版本 URL
 * - 一般用在需要大图预览，查看原图等场景（仅进行较小幅度的默认压缩）
 * @param url 原图 URL
 * @param options 默认缩放限制到指定尺寸以内
 */
export const genImageURL = (url: string, options?: AppendImageOptions) => {
  // 处理原图时，保留 gif 的动态效果，不进行二次处理
  // 强行处理，将变成静态单帧图片
  if (getExtFromFileName(url) === 'gif') return url;

  return appendImageOp(url, {
    format: 'jpg',
    quality: 80,
    // resize: { mode: 'fit', width: 2560, height: 2560 },
    ...options,
  });
};

export const refineImageUrl = (value: ImageRefineOption) => {
  const { previewURL, url, previewOptions, options } = value;

  if (isVideoFile(url)) {
    return {
      previewUrl: previewURL ? genPreviewURL(previewURL, previewOptions || options) : DefaultVideoBg,
    };
  } else {
    return {
      previewUrl: genPreviewURL((previewURL || url)!, previewOptions || options),
      imgUrl: genImageURL((url || previewURL)!, options),
    };
  }
};

export const useRefinedImage = (value: ImageRefineOption) => {
  const { url, previewURL, options, previewOptions } = value;

  const { previewUrl, imgUrl } = useComputed<{ previewUrl: string; imgUrl?: string }>(
    () => refineImageUrl(value),
    [previewURL, url, previewOptions, options],
  );

  return { previewUrl, imgUrl };
};
